/**
 * SavedSegmentBar — save a segment, reopen it, edit it, save it again.
 *
 * Taylor, 2026-09-08: "then just generates donwloads but still have it in the
 * segments and lists page and then save past segments and lists so I could come
 * back tot hem and redownload as needed or open and edit and then save again."
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * A SEGMENT IS A QUERY, NOT A ROW SET
 *
 * `filters` is stored; the matched customers and contacts are not. So reopening
 * a segment from March runs March's CRITERIA against today's book, rather than
 * handing back March's answer wearing today's date.
 *
 * That is also what keeps consent honest. Re-running a contact list goes back
 * through the same suppression the (now retired) Contacts page used, so a person
 * who opts out drops out of every saved segment automatically. A frozen row set
 * would keep serving them forever and nobody would know.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY lastRowCount IS ALWAYS LABELLED WITH A DATE
 *
 * The API returns `lastRunAt` and `lastRowCount` as bookkeeping about the last
 * run — never as the current answer. Rendering "3,214" bare next to a segment
 * name would read as today's count, so it always ships with its timestamp. It is
 * also cleared server-side whenever the filters change, because a count that
 * quietly belongs to the previous definition is worse than no count.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

export interface SavedSegment {
  id: string;
  name: string;
  description: string | null;
  kind: 'customers' | 'contacts';
  filters: Record<string, unknown>;
  isShared: boolean;
  ownerEmail: string;
  isMine: boolean;
  /** Computed server-side so the UI never offers a Save button that 403s. */
  canEdit: boolean;
  lastRunAt: string | null;
  lastRowCount: number | null;
  updatedAt: string;
}

export interface SavedSegmentBarProps {
  /** The filter state to save when "Save this segment" is pressed. */
  currentFilters: Record<string, unknown>;
  /** Called when a saved segment is opened, with its stored filters. */
  onLoad: (filters: Record<string, unknown>, segment: SavedSegment) => void;
  /** What this page's segments select. Drives the consent rules on re-run. */
  kind?: 'customers' | 'contacts';
  /** May this user save at all? (write:segment — not held by `viewer`.) */
  canWrite: boolean;
}

export function SavedSegmentBar({
  currentFilters,
  onLoad,
  kind = 'customers',
  canWrite,
}: SavedSegmentBarProps) {
  const [segments, setSegments] = useState<SavedSegment[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The segment currently loaded, so "save again" updates instead of forking. */
  const [activeId, setActiveId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch<{ segments: SavedSegment[] }>(
        `/api/segments?kind=${encodeURIComponent(kind)}`,
      );
      setSegments(d.segments);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [kind]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveNew = async () => {
    if (!name.trim()) {
      setError(
        'Give the segment a name — one you cannot recognise later is lost, not saved.',
      );
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const d = await apiFetch<{ segment: SavedSegment }>('/api/segments', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          kind,
          filters: currentFilters,
          is_shared: shared,
        }),
      });
      setActiveId(d.segment.id);
      setName('');
      setShared(false);
      setNotice(`Saved “${d.segment.name}”.`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  /** "open and edit and then save again" — update in place, no fork. */
  const updateActive = async () => {
    if (!activeId) return;
    setSaving(true);
    setError(null);
    try {
      const d = await apiFetch<{ segment: SavedSegment }>(
        `/api/segments/${activeId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ filters: currentFilters }),
        },
      );
      setNotice(`Updated “${d.segment.name}” with the current filters.`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (seg: SavedSegment) => {
    setError(null);
    try {
      await apiFetch(`/api/segments/${seg.id}`, { method: 'DELETE' });
      if (activeId === seg.id) setActiveId(null);
      setNotice(`Deleted “${seg.name}”.`);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const toggleShare = async (seg: SavedSegment) => {
    setError(null);
    try {
      await apiFetch(`/api/segments/${seg.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_shared: !seg.isShared }),
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const active = segments.find((s) => s.id === activeId) ?? null;

  return (
    <div className="saved-segments" data-testid="saved-segments">
      <div className="saved-segments-head">
        <button
          className="pill"
          data-testid="saved-segments-toggle"
          onClick={() => setOpen(!open)}
          type="button"
        >
          {open ? 'Hide' : 'Saved segments'}
          {segments.length > 0 ? ` (${segments.length})` : ''}
        </button>

        {canWrite ? (
          <>
            <input
              className="saved-segments-name"
              data-testid="saved-segment-name"
              onChange={(e) => setName(e.target.value)}
              placeholder="Name this segment…"
              value={name}
            />
            <label className="saved-segments-share">
              <input
                checked={shared}
                onChange={(e) => setShared(e.target.checked)}
                type="checkbox"
              />
              <span>Share with the team</span>
            </label>
            <button
              className="btn"
              data-testid="saved-segment-save"
              disabled={saving}
              onClick={saveNew}
              type="button"
            >
              {saving ? 'Saving…' : 'Save as new'}
            </button>
            {/* Only offered when a segment is actually loaded AND yours —
                otherwise "save again" would silently fork somebody's shared
                segment, or 403. */}
            {active?.canEdit ? (
              <button
                className="btn btn-primary"
                data-testid="saved-segment-update"
                disabled={saving}
                onClick={updateActive}
                type="button"
              >
                Save over “{active.name}”
              </button>
            ) : null}
          </>
        ) : (
          <span className="saved-segments-readonly">
            Read-only role — you can open and re-run saved segments, but not
            save one.
          </span>
        )}
      </div>

      {error ? <div className="banner banner-error">{error}</div> : null}
      {notice ? <div className="banner banner-info">{notice}</div> : null}

      {open ? (
        segments.length === 0 ? (
          <div className="saved-segments-empty">
            No saved segments yet. Build a filter above, name it, and save — it
            stores the CRITERIA, so reopening it always returns today&rsquo;s
            answer.
          </div>
        ) : (
          <ul className="saved-segments-list">
            {segments.map((s) => (
              <li className="saved-segment" key={s.id}>
                <div className="saved-segment-main">
                  <button
                    className="link saved-segment-open"
                    onClick={() => {
                      setActiveId(s.id);
                      onLoad(s.filters, s);
                      setNotice(
                        `Opened “${s.name}”. Filters applied; the counts below are live.`,
                      );
                    }}
                    type="button"
                  >
                    {s.name}
                  </button>
                  <div className="saved-segment-meta">
                    {s.isMine ? 'Yours' : `Shared by ${s.ownerEmail}`}
                    {s.isShared ? ' · team-visible' : ' · private'}
                    {/* NEVER a bare count. See the header comment. */}
                    {s.lastRowCount != null && s.lastRunAt
                      ? ` · ${s.lastRowCount.toLocaleString()} rows as of ${fmtWhen(s.lastRunAt)}`
                      : ' · not run yet'}
                  </div>
                </div>
                <div className="saved-segment-actions">
                  {s.canEdit ? (
                    <button
                      className="pill"
                      onClick={() => toggleShare(s)}
                      type="button"
                    >
                      {s.isShared ? 'Make private' : 'Share'}
                    </button>
                  ) : null}
                  {s.canEdit ? (
                    <button
                      className="pill"
                      onClick={() => remove(s)}
                      type="button"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const days = Math.round((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString();
}

export default SavedSegmentBar;
